import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { ScreenTile } from "./ScreenTile";
import { screensOverlap, Rect, applyDrag, clampToBounds, snapToGrid } from "./geometry";
import { AlertTriangle, Plus, RefreshCw, Layout, Smartphone } from "lucide-react";

/**
 * ============================================================================
 * CanvasErrorBoundary Component
 * ============================================================================
 * Catches runtime rendering errors (such as database query failures or undefined values)
 * and displays a styled error message with data-testid="canvas-error".
 */
class CanvasErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("CanvasErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="canvas-error"
          style={{
            maxWidth: "600px",
            margin: "2rem auto",
            padding: "2rem",
            backgroundColor: "var(--color-bg-secondary)",
            border: `1px solid var(--color-error)`,
            borderRadius: "var(--border-radius-lg)",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          <AlertTriangle size={40} color="var(--color-error)" />
          <h3 style={{ fontSize: "1.25rem", fontWeight: 700 }}>Layout Error</h3>
          <p style={{ color: "var(--color-text-secondary)", fontSize: "0.9rem" }}>
            {this.state.error?.message || "An unexpected error occurred while loading the layout configurator."}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: "var(--color-bg-primary)",
              color: "var(--color-text-primary)",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--border-radius-md)",
              padding: "0.5rem 1rem",
              fontSize: "0.85rem",
              fontWeight: 600,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <RefreshCw size={14} />
            Reload Configurator
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * ============================================================================
 * ScreenCanvasContent Component
 * ============================================================================
 * The core component carrying the state and UI rendering of the configurator canvas.
 */
const ScreenCanvasContent: React.FC = () => {
  // 1. Reactive queries to retrieve layout and connected devices
  const layout = useQuery(api.layouts.getUserLayout);
  const computers = useQuery(api.computers.getUserComputers);

  // 2. Mutations to update and create layout configurations
  const updateScreenPosition = useMutation(api.layouts.updateScreenPosition);
  const saveLayout = useMutation(api.layouts.saveLayout);
  const setActiveLayout = useMutation(api.layouts.setActiveLayout);

  // Snapping grid setting (10px) and canvas limits
  // Non-coder tweak: You can change the gridSize here (e.g., to 20 for larger grid snaps)
  // We increase the bounds to width: 6000, height: 3000 to allow larger virtual monitor arrangements
  // without cursor coordinate clamping issues.
  const gridSize = 10;
  const canvasBounds: Rect = { x: 0, y: 0, width: 6000, height: 3000 };

  // Temporary local positions during dragging to calculate collision/overlaps in real-time
  const [tempPositions, setTempPositions] = useState<Record<string, { x: number; y: number }>>({});
  
  // Track client screen width to automatically trigger mobile fallback layout
  const [isMobile, setIsMobile] = useState(false);

  // Hook to handle responsive viewport queries
  useEffect(() => {
    const checkViewport = () => {
      // Treat screens narrower than 768px (standard tablets) as mobile fallbacks
      setIsMobile(window.innerWidth < 768);
    };
    checkViewport();
    window.addEventListener("resize", checkViewport);
    return () => window.removeEventListener("resize", checkViewport);
  }, []);

  /**
   * Action handler: Triggered on PointerMove to update the temporary coordinate mapping
   * and instantly calculate overlaps.
   */
  const handleDragMoveLocal = (monitorId: string, x: number, y: number) => {
    setTempPositions((prev) => ({
      ...prev,
      [monitorId]: { x, y },
    }));
  };

  /**
   * Action handler: Triggered on PointerUp / Drop / Keyboard nudge to commit the final
   * coordinate values to the database.
   */
  const handlePositionChange = async (monitorId: string, x: number, y: number) => {
    if (!layout) return;

    try {
      // Sync coordinates directly to Convex
      await updateScreenPosition({
        layoutId: layout._id,
        monitorId,
        x,
        y,
      });
    } catch (err) {
      console.error("Failed to update screen coordinates:", err);
    } finally {
      // Clear local tracking coordinate for this screen to prevent visual rubber-banding or flicker
      // This is now cleaned up in the finally block so it always runs regardless of success or failure.
      setTempPositions((prev) => {
        const next = { ...prev };
        delete next[monitorId];
        return next;
      });
    }
  };

  /**
   * Action handler: Creates a default layout if no configuration currently exists.
   * Generates a staggered placement pattern based on the list of registered computers.
   */
  const handleCreateDefaultLayout = async () => {
    if (!computers || computers.length === 0) return;

    try {
      // Stagger monitor coordinates to avoid immediate overlap on creation
      const screens = computers.map((comp, idx) => ({
        computerId: comp._id,
        monitorId: "primary",
        // Horizontal spacing: place side-by-side with staggered offsets (e.g. 0, 500, 1000)
        x: idx * 500,
        y: 200,
        // Standard virtual monitor coordinate width/height contracts
        width: 1920,
        height: 1080,
      }));

      // Persist the default configuration
      const layoutId = await saveLayout({
        name: "Default Keyboard/Mouse Layout",
        screens,
        edgeConfig: {
          mode: "instant",
        },
      });

      // Enable the newly generated layout
      await setActiveLayout({ layoutId });
    } catch (err) {
      console.error("Failed to create default layout:", err);
    }
  };

  /**
   * Mobile Accessibility helper: allows adjustments of screen coordinates using buttons.
   * This handles shifting a screen's position by dx and dy, clamping it to the bounds, 
   * snapping it to the layout grid, and clamping again if needed before saving.
   * 
   * For non-coders:
   * - dx: horizontal adjustment (e.g. -50 to nudge left, +50 to nudge right)
   * - dy: vertical adjustment (e.g. -50 to nudge up, +50 to nudge down)
   * - We reuse pure geometry calculations to avoid duplicating logic or introducing layout bugs.
   */
  const handleMobileNudge = async (monitorId: string, dx: number, dy: number) => {
    if (!layout) return;
    const screenIndex = layout.screens.findIndex((s) => s.monitorId === monitorId);
    if (screenIndex === -1) return;

    const screenObj = layout.screens[screenIndex];
    
    // 1. Shift the screen position by dx and dy to get the initial dragged layout rectangle
    const draggedRect = applyDrag(screenObj, dx, dy);
    
    // 2. Keep the dragged rectangle within the outer canvas margins
    const clampedRect = clampToBounds(draggedRect, canvasBounds);
    
    // 3. Align the clamped coordinates to our configuration grid (typically 10px steps)
    const snappedPoint = snapToGrid(clampedRect, gridSize);
    
    // 4. Double check bounds clamping after snapping coordinates to ensure the final layout remains valid
    const finalRect = clampToBounds({ ...clampedRect, ...snappedPoint }, canvasBounds);
    
    // 5. Save the final calculated position back to Convex backend database
    await handlePositionChange(monitorId, finalRect.x, finalRect.y);
  };

  // State 1: Loading State (queries still resolving)
  if (layout === undefined || computers === undefined) {
    return (
      <div
        data-testid="canvas-loading"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "350px",
          gap: "1rem",
          backgroundColor: "var(--color-bg-secondary)",
          borderRadius: "var(--border-radius-lg)",
          border: "1px solid var(--color-border)",
          color: "var(--color-text-secondary)",
        }}
      >
        <RefreshCw className="animate-spin" size={32} color="var(--color-accent)" />
        <p style={{ fontSize: "0.95rem", fontWeight: 500 }}>Loading screen layout configuration...</p>
      </div>
    );
  }

  // State 2: Empty State (active user layout does not exist)
  if (layout === null) {
    const hasDevices = computers.length > 0;

    return (
      <div
        data-testid="canvas-empty"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "3rem 2rem",
          minHeight: "350px",
          textAlign: "center",
          backgroundColor: "var(--color-bg-secondary)",
          borderRadius: "var(--border-radius-lg)",
          border: "1px solid var(--color-border)",
          gap: "1.5rem",
        }}
      >
        <Layout size={48} color="var(--color-text-muted)" />
        <div style={{ maxWidth: "500px" }}>
          <h3 style={{ fontSize: "1.25rem", fontWeight: 700, marginBottom: "0.5rem" }}>
            No Active Monitor Layout
          </h3>
          <p style={{ color: "var(--color-text-secondary)", fontSize: "0.9rem", lineHeight: "1.5" }}>
            {hasDevices
              ? "You have registered computers, but no layout arrangement has been configured yet. Let's create a default monitor arrangement to get started."
              : "Before configuring your multi-monitor keyboard/mouse sharing layout, you must link and register at least one computer node using the desktop client application."}
          </p>
        </div>

        {hasDevices ? (
          <button
            onClick={handleCreateDefaultLayout}
            style={{
              backgroundColor: "var(--color-accent)",
              color: "#000000",
              border: "none",
              borderRadius: "var(--border-radius-md)",
              padding: "0.75rem 1.5rem",
              fontWeight: 700,
              fontSize: "0.9rem",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "background-color 0.2s",
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "var(--color-accent-hover)")}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "var(--color-accent)")}
          >
            <Plus size={16} />
            Create Default Layout
          </button>
        ) : (
          <div
            style={{
              fontSize: "0.85rem",
              color: "var(--color-warning)",
              fontWeight: 600,
              padding: "0.5rem 1rem",
              border: "1px solid var(--color-warning)",
              borderRadius: "var(--border-radius-sm)",
              backgroundColor: "rgba(245, 158, 11, 0.05)",
            }}
          >
            Awaiting device connections...
          </div>
        )}
      </div>
    );
  }

  // 3. Coordinate evaluation & Collision computation for layout screens
  // We substitute screen coordinates with temporary live drag coordinate positions.
  const activeScreens = layout.screens.map((screenObj) => {
    const temp = tempPositions[screenObj.monitorId];
    return {
      ...screenObj,
      x: temp !== undefined ? temp.x : screenObj.x,
      y: temp !== undefined ? temp.y : screenObj.y,
    };
  });

  // Perform full pair-wise intersection calculations
  const overlappingIds = new Set<string>();
  for (let i = 0; i < activeScreens.length; i++) {
    for (let j = i + 1; j < activeScreens.length; j++) {
      const a = activeScreens[i];
      const b = activeScreens[j];
      if (screensOverlap(a, b)) {
        overlappingIds.add(a.monitorId);
        overlappingIds.add(b.monitorId);
      }
    }
  }

  // State 3: Mobile fallback list when viewport is narrow (< 768px)
  if (isMobile) {
    return (
      <div
        data-testid="mobile-list-fallback"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          padding: "1.5rem",
          backgroundColor: "var(--color-bg-secondary)",
          borderRadius: "var(--border-radius-lg)",
          border: "1px solid var(--color-border)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", borderBottom: "1px solid var(--color-border)", paddingBottom: "0.75rem" }}>
          <Smartphone size={20} color="var(--color-accent)" />
          <h3 style={{ fontSize: "1.05rem", fontWeight: 700 }}>Mobile Layout Adjuster</h3>
        </div>
        
        <p style={{ fontSize: "0.85rem", color: "var(--color-text-secondary)", lineHeight: "1.4" }}>
          Pointer dragging is disabled on smaller screens. Use the coordinates adjustment controls below to align your monitors.
        </p>

        {activeScreens.map((scr) => {
          const comp = computers.find((c) => c._id === scr.computerId);
          const compName = comp ? comp.name : "Unknown Computer";
          const isOverlapping = overlappingIds.has(scr.monitorId);

          return (
            <div
              key={scr.monitorId}
              style={{
                border: `1px solid ${isOverlapping ? "var(--color-error)" : "var(--color-border)"}`,
                borderRadius: "var(--border-radius-md)",
                padding: "1rem",
                backgroundColor: "rgba(11, 15, 25, 0.25)",
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>{compName}</span>
                <span style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)", fontFamily: "var(--font-mono)" }}>
                  {scr.monitorId}
                </span>
              </div>

              {isOverlapping && (
                <div style={{ fontSize: "0.7rem", color: "var(--color-error)", fontWeight: 700, textTransform: "uppercase" }}>
                  ⚠️ Collision Overlap Warning
                </div>
              )}

              {/* X coordinate controls */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "0.85rem" }}>
                <span>X Offset:</span>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <button
                    onClick={() => handleMobileNudge(scr.monitorId, -50, 0)}
                    style={{ padding: "0.25rem 0.5rem", borderRadius: "var(--border-radius-sm)", border: "1px solid var(--color-border)", cursor: "pointer", backgroundColor: "var(--color-bg-primary)", color: "var(--color-text-primary)" }}
                  >
                    -50
                  </button>
                  <span style={{ fontFamily: "var(--font-mono)", fontWeight: 600, minWidth: "50px", textAlign: "center" }}>
                    {scr.x}px
                  </span>
                  <button
                    onClick={() => handleMobileNudge(scr.monitorId, 50, 0)}
                    style={{ padding: "0.25rem 0.5rem", borderRadius: "var(--border-radius-sm)", border: "1px solid var(--color-border)", cursor: "pointer", backgroundColor: "var(--color-bg-primary)", color: "var(--color-text-primary)" }}
                  >
                    +50
                  </button>
                </div>
              </div>

              {/* Y coordinate controls */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "0.85rem" }}>
                <span>Y Offset:</span>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  {/* Fixed Y Offset decrease button to shift vertically by dy = -50 instead of dx = -50 */}
                  <button
                    onClick={() => handleMobileNudge(scr.monitorId, 0, -50)}
                    style={{ padding: "0.25rem 0.5rem", borderRadius: "var(--border-radius-sm)", border: "1px solid var(--color-border)", cursor: "pointer", backgroundColor: "var(--color-bg-primary)", color: "var(--color-text-primary)" }}
                  >
                    -50
                  </button>
                  <span style={{ fontFamily: "var(--font-mono)", fontWeight: 600, minWidth: "50px", textAlign: "center" }}>
                    {scr.y}px
                  </span>
                  <button
                    onClick={() => handleMobileNudge(scr.monitorId, 0, 50)}
                    style={{ padding: "0.25rem 0.5rem", borderRadius: "var(--border-radius-sm)", border: "1px solid var(--color-border)", cursor: "pointer", backgroundColor: "var(--color-bg-primary)", color: "var(--color-text-primary)" }}
                  >
                    +50
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // State 4: Standard Interactive Canvas View (Default)
  return (
    <div
      data-testid="canvas-container"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        width: "100%",
      }}
    >
      {/* Visual Canvas Info and Overlap Warning block */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "0.5rem",
        }}
      >
        <div style={{ fontSize: "0.85rem", color: "var(--color-text-secondary)" }}>
          {/* We display the canvas boundary size dynamically here */}
          Drag screens to align. Boundary size: <strong>{canvasBounds.width}px × {canvasBounds.height}px</strong>. Grid: <strong>{gridSize}px</strong>.
        </div>
        
        {overlappingIds.size > 0 && (
          <div
            data-testid="canvas-overlap-banner"
            style={{
              fontSize: "0.8rem",
              color: "var(--color-error)",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
            }}
          >
            <AlertTriangle size={14} />
            Screens overlap! Red outlined monitors cannot be saved correctly.
          </div>
        )}
      </div>

      {/* Outer Scroll Window hosting the canvas */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "600px",
          overflow: "auto",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--border-radius-lg)",
          backgroundColor: "var(--color-bg-secondary)",
          boxShadow: "inset 0 4px 12px rgba(0, 0, 0, 0.25)",
        }}
      >
        {/* Inner Canvas grid area */}
        <div
          data-testid="canvas-area"
          style={{
            position: "relative",
            width: `${canvasBounds.width}px`,
            height: `${canvasBounds.height}px`,
            backgroundColor: "#080c14",
            backgroundImage: "radial-gradient(#1e293b 1px, transparent 1px)",
            backgroundSize: "20px 20px",
          }}
        >
          {/* 
            BUG FIX: Map over activeScreens (which contains live temporary dragging positions)
            instead of layout.screens to ensure visual feedback matches the calculations.
          */}
          {activeScreens.map((scr) => {
            const comp = computers.find((c) => c._id === scr.computerId);
            const compName = comp ? comp.name : "Unknown Computer";

            return (
              <ScreenTile
                key={scr.monitorId}
                screen={scr}
                computerName={compName}
                gridSize={gridSize}
                canvasBounds={canvasBounds}
                isOverlapping={overlappingIds.has(scr.monitorId)}
                onDragMoveLocal={handleDragMoveLocal}
                onPositionChange={handlePositionChange}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Wrap content inside error boundary to fulfill robust error display requirements
export const ScreenCanvas: React.FC = () => {
  return (
    <CanvasErrorBoundary>
      <ScreenCanvasContent />
    </CanvasErrorBoundary>
  );
};
